import { useEffect, useState } from 'react';
import { api } from '../../api/client';
import type { CarouselEvent, Member } from '../../types';
import { useFaceDetection } from '../../hooks/useFaceDetection';
import { FaceTagRow, type FaceTagRowFace } from '../../components/FaceTagRow';
import { PersonPicker } from '../../components/PersonPicker';
import { BetaBadge } from '../../components/BetaBadge';

// Face *recognition/matching* (auto-clustering similar faces, suggesting
// "this might be X") was removed entirely — creating a facial-geometry
// template used to identify someone is exactly what biometric-privacy laws
// like Illinois' BIPA regulate, and that risk doesn't go away just because
// an account holder consented, since most of the people actually being
// tagged here (children, other relatives) never gave that consent
// themselves. This page is now a plain backlog of photos missing a tag,
// with face *detection* (bounding boxes only, no identity) used purely to
// make tapping the right spot on a photo easier.

const HUMAN_MEMBER_STATUSES = new Set(['active', 'no_email', 'pending']);

interface UntaggedPhoto {
  photoId: string;
  eventId: string;
  eventTitle: string;
  filePath: string;
  uploadedAt: string;
  taggedUserIds: string[];
}

function buildUntaggedList(events: CarouselEvent[], members: Member[]): UntaggedPhoto[] {
  const humanMemberIds = new Set(
    members.filter(m => !m.petType && HUMAN_MEMBER_STATUSES.has(m.status)).map(m => m.user.id)
  );
  if (humanMemberIds.size === 0) return [];

  const refs: UntaggedPhoto[] = [];
  for (const event of events) {
    for (const photo of event.photos ?? []) {
      const taggedUserIds = ((photo.participants ?? []) as { userId: string }[]).map(p => p.userId);
      const taggedSet = new Set(taggedUserIds);
      const hasUntagged = [...humanMemberIds].some(id => !taggedSet.has(id));
      if (!hasUntagged) continue;
      refs.push({
        photoId: photo.id,
        eventId: event.id,
        eventTitle: event.title,
        filePath: photo.filePath,
        uploadedAt: photo.uploadedAt,
        taggedUserIds,
      });
    }
  }
  refs.sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime());
  return refs;
}

export function FacesReviewPage() {
  const [phase, setPhase] = useState<'loading' | 'ready'>('loading');
  const [photos, setPhotos] = useState<UntaggedPhoto[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [openPhotoId, setOpenPhotoId] = useState<string | null>(null);
  const [photoFaces, setPhotoFaces] = useState<Record<string, FaceTagRowFace[]>>({});
  const [detectingPhotoId, setDetectingPhotoId] = useState<string | null>(null);
  const [markingPhotoId, setMarkingPhotoId] = useState<string | null>(null);
  const [markingBusy, setMarkingBusy] = useState(false);
  const [markingError, setMarkingError] = useState<string | null>(null);
  const [pendingFace, setPendingFace] = useState<{ photo: UntaggedPhoto; cropUrl: string } | null>(null);

  const { detectFaces, describeFaceAtPoint } = useFaceDetection();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setPhase('loading');
      setError(null);
      try {
        const [eventsRes, membersRes] = await Promise.all([
          api.get<{ events: CarouselEvent[] }>('/events', { params: { allPhotos: 'true' } }),
          api.get<Member[]>('/account/members'),
        ]);
        if (cancelled) return;
        const events = Array.isArray(eventsRes.data?.events) ? eventsRes.data.events : [];
        const loadedMembers = Array.isArray(membersRes.data) ? membersRes.data : [];
        setMembers(loadedMembers);
        setPhotos(buildUntaggedList(events, loadedMembers));
        setPhase('ready');
      } catch {
        if (!cancelled) {
          setError('Failed to load photos.');
          setPhase('ready');
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const humanMembers = members.filter(m => !m.petType && HUMAN_MEMBER_STATUSES.has(m.status));

  function openPhoto(photo: UntaggedPhoto) {
    setOpenPhotoId(v => (v === photo.photoId ? null : photo.photoId));
    setMarkingPhotoId(null);
    setMarkingError(null);
    if (photo.photoId in photoFaces) return;
    setDetectingPhotoId(photo.photoId);
    detectFaces(photo.filePath).then(faces => {
      setPhotoFaces(prev => ({
        ...prev,
        [photo.photoId]: faces.map(f => ({ cropUrl: f.cropUrl, assignedUserId: null })),
      }));
      setDetectingPhotoId(null);
    }).catch(() => setDetectingPhotoId(null));
  }

  /** Removes a photo from the backlog once every human circle member is tagged in it. */
  function updateTaggedIds(photo: UntaggedPhoto, updater: (ids: string[]) => string[]) {
    setPhotos(prev => {
      const next = prev.map(p => (p.photoId === photo.photoId ? { ...p, taggedUserIds: updater(p.taggedUserIds) } : p));
      const updated = next.find(p => p.photoId === photo.photoId);
      if (updated) {
        const humanMemberIds = humanMembers.map(m => m.user.id);
        const stillUntagged = humanMemberIds.some(id => !updated.taggedUserIds.includes(id));
        if (!stillUntagged) {
          setOpenPhotoId(v => (v === photo.photoId ? null : v));
          return next.filter(p => p.photoId !== photo.photoId);
        }
      }
      return next;
    });
  }

  async function assignFaceToMember(photo: UntaggedPhoto, faceIdx: number, userId: string | null) {
    const prevUserId = (photoFaces[photo.photoId] ?? [])[faceIdx]?.assignedUserId ?? null;
    setPhotoFaces(prev => ({
      ...prev,
      [photo.photoId]: (prev[photo.photoId] ?? []).map((f, fi) => (fi !== faceIdx ? f : { ...f, assignedUserId: userId })),
    }));
    if (userId) {
      await api.post(`/events/${photo.eventId}/photos/${photo.photoId}/participants`, { userId });
      await api.post(`/events/${photo.eventId}/participants`, { userId }).catch(() => {});
      updateTaggedIds(photo, ids => Array.from(new Set([...ids, userId])));
    } else if (prevUserId) {
      await api.delete(`/events/${photo.eventId}/photos/${photo.photoId}/participants/${prevUserId}`);
      updateTaggedIds(photo, ids => ids.filter(id => id !== prevUserId));
    }
  }

  async function tagWithoutFace(photo: UntaggedPhoto, userId: string) {
    await api.post(`/events/${photo.eventId}/photos/${photo.photoId}/participants`, { userId });
    await api.post(`/events/${photo.eventId}/participants`, { userId }).catch(() => {});
    updateTaggedIds(photo, ids => Array.from(new Set([...ids, userId])));
  }

  async function addNewPerson(name: string): Promise<string> {
    const { data } = await api.post('/account/members', { name });
    setMembers(prev => [...prev, data]);
    return data.user.id;
  }

  async function handleMarkFace(photo: UntaggedPhoto, xNorm: number, yNorm: number) {
    setMarkingBusy(true);
    setMarkingError(null);
    const face = await describeFaceAtPoint(photo.filePath, xNorm, yNorm);
    setMarkingBusy(false);
    if (!face) {
      setMarkingError("Couldn't find a face there — try tapping more precisely, or a different spot.");
      return;
    }
    setMarkingPhotoId(null);
    // Marking a face always means identifying it right away — go straight to
    // the picker instead of adding an unselected crop to the row and hoping
    // the user notices it.
    setPendingFace({ photo, cropUrl: face.cropUrl });
  }

  async function assignPendingFace(userId: string) {
    if (!pendingFace) return;
    const { photo, cropUrl } = pendingFace;
    setPhotoFaces(prev => ({
      ...prev,
      [photo.photoId]: [...(prev[photo.photoId] ?? []), { cropUrl, assignedUserId: userId }],
    }));
    setPendingFace(null);
    await api.post(`/events/${photo.eventId}/photos/${photo.photoId}/participants`, { userId });
    await api.post(`/events/${photo.eventId}/participants`, { userId }).catch(() => {});
    updateTaggedIds(photo, ids => Array.from(new Set([...ids, userId])));
  }

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">Faces <BetaBadge /></h1>
        <p className="text-sm text-slate-500 mt-1">
          Photos where someone in your circle isn't tagged yet — tap one to mark who's in it.
        </p>
      </div>

      {error && <p className="text-sm text-red-500 mb-4">{error}</p>}
      {phase === 'loading' && <p className="text-sm text-slate-400">Loading your photos…</p>}
      {phase === 'ready' && photos.length === 0 && (
        <p className="text-sm text-slate-400">No untagged photos found — everyone's already tagged.</p>
      )}

      {photos.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {photos.map(photo => {
            const isOpen = openPhotoId === photo.photoId;
            const faces = photoFaces[photo.photoId] ?? [];
            const isDetecting = detectingPhotoId === photo.photoId;
            const faceAssignedIds = new Set(faces.map(f => f.assignedUserId).filter(Boolean) as string[]);
            const undetectedMembers = humanMembers.filter(
              m => !photo.taggedUserIds.includes(m.user.id) && !faceAssignedIds.has(m.user.id)
            );

            return (
              <div key={photo.photoId} className="rounded-xl border border-indigo-50 bg-white shadow-sm overflow-hidden">
                <button type="button" onClick={() => openPhoto(photo)} className="block w-full aspect-square bg-slate-100">
                  <img src={photo.filePath} alt="" loading="lazy" className="w-full h-full object-cover" />
                </button>
                <div className="px-2 py-1.5">
                  <p className="text-[11px] text-slate-400 truncate">{photo.eventTitle}</p>
                </div>

                {isOpen && (
                  <div className="border-t border-indigo-50 px-2.5 py-2.5 max-h-72 overflow-y-auto">
                    {isDetecting && (
                      <p className="text-xs text-violet-600 flex items-center gap-1.5 mb-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-violet-500 animate-pulse" />
                        Detecting faces…
                      </p>
                    )}

                    {!isDetecting && faces.length === 0 && (
                      <button
                        type="button"
                        onClick={() => { setMarkingPhotoId(photo.photoId); setMarkingError(null); }}
                        className="text-xs font-medium text-indigo-600 hover:text-indigo-800 mb-2"
                      >
                        No face detected automatically — tap on the photo to mark it
                      </button>
                    )}

                    {!isDetecting && faces.length > 0 && (
                      <FaceTagRow
                        faces={faces}
                        members={humanMembers}
                        onAssign={(faceIdx, userId) => assignFaceToMember(photo, faceIdx, userId)}
                        onAddNewPerson={async (faceIdx, name) => {
                          const userId = await addNewPerson(name);
                          await assignFaceToMember(photo, faceIdx, userId);
                        }}
                      />
                    )}

                    {undetectedMembers.length > 0 && (
                      <div className="mt-2">
                        <p className="text-[11px] font-medium text-slate-400 mb-1">Also tag manually:</p>
                        <div className="flex flex-wrap gap-1.5">
                          {undetectedMembers.map(m => (
                            <button
                              key={m.user.id}
                              type="button"
                              onClick={() => tagWithoutFace(photo, m.user.id)}
                              className="text-xs px-2.5 py-1 rounded-full border border-slate-200 text-slate-600 hover:border-indigo-300 hover:text-indigo-600 transition-colors"
                            >
                              {m.user.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {markingPhotoId && (() => {
        const photo = photos.find(p => p.photoId === markingPhotoId);
        if (!photo) return null;
        return (
          <div
            className="fixed inset-0 z-50 bg-black/90 flex flex-col items-center justify-center p-4"
            onClick={() => { setMarkingPhotoId(null); setMarkingError(null); }}
          >
            <img
              src={photo.filePath}
              alt=""
              className="max-w-[95vw] max-h-[75vh] object-contain cursor-crosshair select-none"
              onClick={e => {
                e.stopPropagation();
                const rect = e.currentTarget.getBoundingClientRect();
                handleMarkFace(photo, (e.clientX - rect.left) / rect.width, (e.clientY - rect.top) / rect.height);
              }}
            />
            <div className="mt-4 text-center max-w-sm">
              <p className="text-sm text-white font-medium">{markingBusy ? 'Looking for a face there…' : 'Tap their face'}</p>
              {markingError && <p className="text-xs text-red-300 mt-1">{markingError}</p>}
            </div>
            <button
              onClick={e => { e.stopPropagation(); setMarkingPhotoId(null); setMarkingError(null); }}
              aria-label="Cancel marking"
              className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white text-xl flex items-center justify-center transition-colors"
            >
              ×
            </button>
          </div>
        );
      })()}

      {pendingFace && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => setPendingFace(null)}
        >
          <div className="w-full max-w-xs" onClick={e => e.stopPropagation()}>
            <PersonPicker
              members={humanMembers}
              assignedUserId={null}
              onAssign={assignPendingFace}
              onAddNewPerson={async name => assignPendingFace(await addNewPerson(name))}
              onClose={() => setPendingFace(null)}
              headerCropUrl={pendingFace.cropUrl}
              headerLabel="Who is this?"
            />
          </div>
        </div>
      )}
    </div>
  );
}
